import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { useChatStore } from "@stores/chat.store";
import { CHAT_MAX_NOTIFICATION_COUNTER } from "@/utils/constants";

import { measureTabOffsets } from "./utils/tabs.helpers";

import type {
  TTabsConfig,
  TSliderCss,
  TTabItem,
  TTabsLogicResult,
} from "./tabs.types";

/** Calcula posição do slider, contadores de não-lidas e itens renderizáveis das tabs. */
export const useTabsLogic = ({
  itemList,
  reducerName,
}: TTabsConfig): TTabsLogicResult => {
  const storeSelectedTabIndex = useChatStore((s) => s.tabs[reducerName]);
  const unreadChats = useChatStore((s) => s.conversation.unreadChats);
  const refTitleContent = useRef<HTMLDivElement>(null);

  const [tabOffsets, setTabOffsets] = useState<number[] | null>(null);

  const sliderCss = useMemo<TSliderCss | undefined>(() => {
    if (!tabOffsets) return undefined;
    return { transform: `translateX(${tabOffsets[storeSelectedTabIndex]}px)` };
  }, [tabOffsets, storeSelectedTabIndex]);

  useLayoutEffect(() => {
    const update = () => {
      if (refTitleContent.current) {
        setTabOffsets(measureTabOffsets(refTitleContent.current));
      }
    };

    const timer = setTimeout(update, 500);
    globalThis.addEventListener("resize", update, false);
    return () => {
      clearTimeout(timer);
      globalThis.removeEventListener("resize", update, false);
    };
  }, []);

  const tabItems: TTabItem[] = useMemo(() =>
    itemList.map((item, index) => {
      const active = storeSelectedTabIndex === index;
      const tab = item.title === "Comprando" ? "from" : "to";
      // Contador das abas vem só do getUnreadCount (unreadFrom/unreadTo via socket),
      // fonte única da verdade — sem recontar pela lista filtrada. ANCR-5329.
      const unreadCount = unreadChats[tab];
      const unreadChatsCounter =
        unreadCount < CHAT_MAX_NOTIFICATION_COUNTER ? unreadCount : "+99";
      const resolvedStyle =
        typeof item.customStyle === "function"
          ? item.customStyle()
          : item.customStyle;
      return {
        title: item.title,
        active,
        resolvedStyle,
        unreadChats: unreadCount,
        unreadChatsCounter,
        clickAction: item.clickAction,
        index,
      };
    }),
    [itemList, storeSelectedTabIndex, unreadChats],
  );

  const isEmpty = !itemList.length;

  return {
    refTitleContent,
    sliderCss,
    tabItems,
    isEmpty,
  };
};
